import React, { useState, useEffect, useCallback } from "react";
import { Dialog, Dropdown, Button, toastMessage } from "./index";
import { useGetVehiclesMutationMutation, useAddVehicleMutation, useUpdateVehicleNumberInTripMutation } from "../../api/vmsSlice";
import { useUpdateManageTripMutation } from "../../api/api";
import { debounce } from "../../services/functions";
import { buttonClasses } from "./theme";

export default function EditVehicleDialog({ open, onClose, tripData, onSuccess }) {
	const [newVehicleNumber, setNewVehicleNumber] = useState(null);
	const [vehicleOptions, setVehicleOptions] = useState([]);
	const [searchInput, setSearchInput] = useState("");
	const [conflictState, setConflictState] = useState(null); // null | { message, existingVehicleEntryId }
	const [getVehicleTrigger, { isLoading: isVehicleLoading }] = useGetVehiclesMutationMutation();
	const [updateVehicleInTrip, { isLoading: isUpdating }] = useUpdateVehicleNumberInTripMutation();
	const [updateManageTrip, { isLoading: isTerminating }] = useUpdateManageTripMutation();
	const [addVehicleTrigger, { isLoading: isAddingVehicle }] = useAddVehicleMutation();

	const fetchVehicles = async (search = "") => {
		try {
			const response = await getVehicleTrigger({
				page_no: "1",
				limit: "25",
				searchBy: "vehicle",
				search,
			});
			const vehicles = response?.data?.data || [];
			setVehicleOptions(
				vehicles.map((v) => ({
					label: v.truck_no,
					value: v._id,
				}))
			);
		} catch (err) {
			console.error("Error fetching vehicles:", err);
		}
	};

	const debouncedSearch = useCallback(debounce((value) => {
		fetchVehicles(value);
	}, 400), []);

	// Fetch initial 25 vehicles when dialog opens
	useEffect(() => {
		if (open) {
			fetchVehicles("");
			setNewVehicleNumber(null);
			setSearchInput("");
			setConflictState(null);
		}
	}, [open]);

	const handleVehicleSearch = (e) => {
		const searchValue = e?.target?.value || "";
		setSearchInput(searchValue);
		setNewVehicleNumber(null);
		debouncedSearch(searchValue);
	};

	const handleAddVehicle = async () => {
		if (!searchInput.trim()) return;
		try {
			const payload = {
				reg_no: searchInput.trim(),
				truck_no: searchInput.trim(),
				owner_Name: "",
				formData: { driverName: "", driverNumber: "" },
				template_name: "vehicle",
			};
			const res = await addVehicleTrigger({ ...payload });
			const { error, message } = res?.data || {};
			if (error) {
				return toastMessage(false, message);
			}
			toastMessage(true, message || "Vehicle added successfully");
			const response = await getVehicleTrigger({
				page_no: "1",
				limit: "25",
				searchBy: "vehicle",
				search: searchInput.trim(),
			});
			const vehicles = response?.data?.data || [];
			const options = vehicles.map((v) => ({ label: v.truck_no, value: v._id }));
			setVehicleOptions(options);
			const added = options.find((o) => o.label === searchInput.trim());
			if (added) setNewVehicleNumber(added);
		} catch (err) {
			toastMessage(false, err?.message || "Failed to add vehicle");
		}
	};

	const handleClose = () => {
		setNewVehicleNumber(null);
		setVehicleOptions([]);
		setSearchInput("");
		setConflictState(null);
		onClose();
	};

	const handleSubmit = async () => {
		if (!newVehicleNumber || !tripData) return;
		try {
			const payload = {
				vehicleEntryId: tripData._id,
				newVehicleNumber: newVehicleNumber.label,
				newVehicleId: newVehicleNumber.value,
			};
			const res = await updateVehicleInTrip(payload).unwrap();
			if (res?.success) {
				toastMessage(true, res.message || "Vehicle number updated successfully");
				handleClose();
				onSuccess?.();
			} else if (res?.isTripExists) {
				setConflictState({
					message: res.message || "Another ongoing trip exists for this vehicle.",
					existingVehicleEntryId: res.data._id,
				});
			} else {
				toastMessage(false, res?.message || "Failed to update vehicle number");
			}
		} catch (err) {
			toastMessage(false, err?.data?.message || err?.message || "Failed to update vehicle number");
		}
	};

	const handleTerminateAndUpdate = async () => {
		// console.log("Conflict State:", conflictState);
		if (!conflictState?.existingVehicleEntryId) return;
		try {
			// Step 1: Terminate the existing conflicting trip
			const terminatePayload = {
				vehicleEntryId: conflictState.existingVehicleEntryId,
				forceEndTrip: true,
				cancelTrip: false,
				data: {
					remark: `Vehicle number is updated in trip ${tripData?.trip_counter || ""}, so this trip is terminated.`,
				},
			};
			const terminateRes = await updateManageTrip(terminatePayload).unwrap();
			if (terminateRes?.error) {
				return toastMessage(false, terminateRes.message || "Failed to terminate existing trip");
			}
			toastMessage(true, terminateRes.message || "Existing trip terminated successfully");

			// Step 2: Now update the vehicle number in the current trip
			setConflictState(null);
			const updatePayload = {
				vehicleEntryId: tripData._id,
				newVehicleNumber: newVehicleNumber.label,
				newVehicleId: newVehicleNumber.value,
			};
			const updateRes = await updateVehicleInTrip(updatePayload).unwrap();
			if (updateRes?.success) {
				toastMessage(true, updateRes.message || "Vehicle number updated successfully");
				handleClose();
				onSuccess?.();
			} else {
				toastMessage(false, updateRes?.message || "Failed to update vehicle number");
			}
		} catch (err) {
			toastMessage(false, err?.data?.message || err?.message || "Failed to terminate existing trip");
		}
	};

	const handleGoBack = () => {
		setConflictState(null);
	};

	const showAddButton = searchInput.trim() && vehicleOptions.length === 0 && !isVehicleLoading;

	// Conflict / terminate confirmation view
	if (conflictState) {
		return (
			<Dialog
				open={open}
				handleClose={handleClose}
				title={`Edit Vehicle - Trip #${tripData?.trip_counter || ""}`}
				size="sm"
				noBtn
			>
				<div style={{ padding: "8px 0", display: "flex", flexDirection: "column", gap: "16px" }}>
					<div
						style={{
							backgroundColor: "#fff3e0",
							border: "1px solid #ff9800",
							borderRadius: "8px",
							padding: "12px 16px",
							fontSize: "14px",
							color: "#e65100",
						}}
					>
						{conflictState.message}
					</div>
					<p style={{ margin: 0, fontSize: "14px", color: "#333" }}>
						Do you want to terminate the other ongoing trip and update the vehicle number?
					</p>
					<div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
						<Button
							text="Go Back"
							style={buttonClasses.lynkitOrangeEmpty}
							onClick={handleGoBack}
						/>
						<Button
							text={isTerminating ? "Terminating..." : "Yes, Terminate"}
							style={buttonClasses.lynkitOrangeFill}
							onClick={handleTerminateAndUpdate}
							disabled={isTerminating}
						/>
					</div>
				</div>
			</Dialog>
		);
	}

	// Normal edit form view
	return (
		<Dialog
			open={open}
			handleClose={handleClose}
			title={`Edit Vehicle - Trip #${tripData?.trip_counter || ""}`}
			size="sm"
			handleCancel={handleClose}
			handleSubmit={handleSubmit}
			SubmitText={isUpdating ? "Updating..." : "Update Vehicle Number"}
			isSubmitDisabled={!newVehicleNumber || isUpdating}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "8px 0" }}>
				<div>
					<label style={{ fontSize: "13px", color: "#666", marginBottom: "4px", display: "block" }}>Current Vehicle Number</label>
					<input
						type="text"
						value={tripData?.vehicleNumber || ""}
						disabled
						style={{
							width: "100%",
							padding: "10px 12px",
							borderRadius: "12px",
							border: "1px solid #ccc",
							backgroundColor: "#f5f5f5",
							fontSize: "14px",
							color: "#666",
							boxSizing: "border-box",
						}}
					/>
				</div>
				<div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
					<div style={{ flex: 1 }}>
						<Dropdown
							label="New Vehicle Number"
							options={vehicleOptions}
							value={newVehicleNumber}
							onChange={(val) => {
								if (val === null) {
									fetchVehicles("");
								}
								setNewVehicleNumber(val);
								setSearchInput("");
							}}
							onInputChange={handleVehicleSearch}
							clearOnBlur={false}
							loading={isVehicleLoading}
						/>
					</div>
					{showAddButton && (
						<button
							onClick={handleAddVehicle}
							disabled={isAddingVehicle}
							style={{
								backgroundColor: "#4caf50",
								color: "#fff",
								border: "none",
								borderRadius: "12px",
								padding: "8px 16px",
								fontSize: "14px",
								fontWeight: 600,
								cursor: isAddingVehicle ? "not-allowed" : "pointer",
								opacity: isAddingVehicle ? 0.7 : 1,
								whiteSpace: "nowrap",
								marginTop: "2px",
								height: "40px",
							}}
						>
							{isAddingVehicle ? "Adding..." : "Add"}
						</button>
					)}
				</div>
			</div>
		</Dialog>
	);
}
